/**
 * Instantiable API façades over the generated 0.99 SDK functions.
 *
 * `new UsersApi().readUserMe()` still works. Arguments may be the 0.99
 * `{ path, query, body }` shape or the older flat `{ skip, requestBody, userId }`
 * shape — `toSdkOptions` remaps them.
 */
import {
  getGtaReferralSnapshot,
  listGtaReferralReports,
  patchGtaReferralField,
} from '../../client/sdk.gen'
import type { GtaFieldCatalogItem } from '../../client/types.gen'
import { createSdkWrapper, sdkData } from './sdkCall'

export interface ClaimSummaryRecentRunRecord {
  id: number
  referralId: number
  sourceRef?: string | null
  hireStatus?: string | null
  taskId?: string | null
  userId?: number | null
  tenantId?: string | null
  workflowId?: number | null
  version: string
  status: string
  summaryExcerpt?: string | null
  errorMessage?: string | null
  generationTime?: number | null
  cacheHit: boolean
  modelVersion?: string | null
  createdDatetime: string
  updatedDatetime?: string | null
  feedbackRating?: string | null
}

export interface ClaimSummaryRecentRunsResponse {
  data: Array<ClaimSummaryRecentRunRecord>
  count: number
}

export interface ReportTemplatePreviewResponse {
  html: string
}

export interface WorkflowBillingLedgerSummary {
  partner_id: number
  billing_period: string
  total_runs: number
  pending_runs: number
  billed_runs: number
  excluded_runs: number
  capped_runs: number
  evaluation_runs: number
}

export interface WorkflowBillingLedgerEventRow {
  id: number
  partner_id?: number | null
  workflow_key: string
  referral_id?: number | null
  task_id?: string | null
  billing_type: string
  billing_period: string
  status: string
  invoice_id?: number | null
  started_at: string
  evaluation_mode: boolean
  created_datetime?: string
  updated_datetime?: string | null
}

export type AiClaimsDashboardSummary =
  import('../../client/types.gen').AiClaimsDashboardSummaryPublic
export type AiClaimsWorkflowCard = import('../../client/types.gen').WorkflowCardPublic
export interface AiClaimsWorkflowCardsResponse {
  data: Array<AiClaimsWorkflowCard>
}

export interface BulkOperationReferralRow {
  referral_id: number
  claim_id: number | null
  client_firstname: string | null
  client_lastname: string | null
  client_vehicle_reg: string | null
}

export interface BulkOperationReferralsResponse {
  data: BulkOperationReferralRow[]
  count: number
}

export interface TDataGetBulkOperationReferrals {
  operationId: number
  skip?: number
  limit?: number
}

export type CallAnalysisJob = import('../../client/types.gen').CallAnalysisJobPublic
export type CallAnalysisJobsResponse = import('../../client/types.gen').CallAnalysisJobsPublic
export type CallAnalysisWorkflowsResponse =
  import('../../client/types.gen').CallAnalysisWorkflowsPublic

export type CallAuditFieldResult = {
  name: string
  label?: string
  severity?: string
  policy_value?: string
  spoken_value?: string
  result: string
  notes?: string
  soft_skills_flag?: boolean
}

export type CallAuditComplianceFlag = {
  severity: string
  timestamp?: string
  issue?: string
  transcript_evidence?: string
  rule_breached?: string
}

export type CallAuditAssessmentItem = {
  area?: string
  rating?: string | number
  comment?: string
  examples?: Array<string | { time?: string; quote?: string; text?: string }>
}

export type CallAuditConductItem = CallAuditAssessmentItem

export type CallAuditReview = {
  pass_fail?: string
  score_pct?: number
  auto_fail_reason?: string | string[] | null
  summary?: string
  conduct_rating?: string
  conduct_rating_reason?: string
  fields?: CallAuditFieldResult[]
  compliance_flags?: CallAuditComplianceFlag[]
  conduct?: CallAuditConductItem[]
  soft_skills?: Record<string, string>
  recommendations?: string[]
  _score_breakdown?: {
    passed: number
    failed: number
    not_asked: number
    unclear: number
    counted: number
  }
}

export type CallAuditResult = {
  runId: string
  generatedAt: string
  quoteFields: Record<string, unknown>
  utterances: Record<string, unknown>[]
  review: CallAuditReview
  agentRunId?: string | null
  uploadId?: number | null
  referralId?: number | null
  reportChannels?: string[]
}

export const CALL_TRANSCRIPTION_STT_SETTING_KEY =
  'CALL_TRANSCRIPTION_STT_PROVIDER'

export const CALL_TRANSCRIPTION_STT_OPTIONS = [
  'deepgram-nova-3',
  'assemblyai-universal-3-pro',
] as const

const STT_PROVIDER_LABELS: Record<string, string> = {
  'deepgram-nova-3': 'Deepgram Nova-3 Monolingual',
  'assemblyai-universal-3-pro': 'AssemblyAI Universal-3 Pro',
}

export function callTranscriptionProviderLabel(provider: string): string {
  return STT_PROVIDER_LABELS[provider] ?? provider
}

export type CallAuditBatchListItem = {
  batchId: string
  status: string
  clientReference?: string | null
  callAgentName?: string | null
  submittedByName?: string | null
  createdAt: string
  callCount: number
  passFail?: string | null
}

export type CallAuditBatchesResponse = {
  data: CallAuditBatchListItem[]
  count: number
}

export type CallAuditBatchDetail = {
  batchId: string
  status: string
  review?: Record<string, unknown> | null
  jobIds: number[]
  reportChannels?: string[]
}

export type {
  ReportTemplateCreate,
  ReportTemplatePublic,
  ReportTemplatesPublic,
  ReportTemplateUpdate,
} from '../../client/types.gen'

export const AdministrationApi = createSdkWrapper('AdministrationService')
export const AiPromptConfigsApi = createSdkWrapper('AiPromptConfigsService')
export const AnniesApi = createSdkWrapper('AnniesService')
export const ApiBillingApi = createSdkWrapper('ApiBillingService')
export const AuthoritiesApi = createSdkWrapper('AuthoritiesService')
export const CircsGradesApi = createSdkWrapper('CircsGradesService')
export const ClaimsApi = createSdkWrapper('ClaimsService')
export const ComplaintsApi = createSdkWrapper('ComplaintsService')
export const InsurersApi = createSdkWrapper('InsurersService')
export const InvoicesApi = createSdkWrapper('InvoicesService')
export const LoginApi = createSdkWrapper('LoginService')
export const OtpApi = createSdkWrapper('OtpService')
export const PartnerRulesApi = createSdkWrapper('PartnerRulesService')
export const PartnersApi = createSdkWrapper('PartnersService')
export const RateLimitingApi = createSdkWrapper('RateLimitingService')
export const ReferralAllocationsApi = createSdkWrapper('ReferralAllocationsService')
export const ReferralNotesApi = createSdkWrapper('ReferralNotesService')
export const ReferralsApi = createSdkWrapper('ReferralsService')
export const RepairBillingApi = createSdkWrapper('RepairBillingService')
export const ReportTemplatesApi = createSdkWrapper('ReportTemplatesService')
export const RequestsApi = createSdkWrapper('RequestsService')
export const RoleAllocationsApi = createSdkWrapper('RoleAllocationsService')
export const RolePermissionsApi = createSdkWrapper('RolePermissionsService')
export const RolesApi = createSdkWrapper('RolesService')
export const SchedulerApi = createSdkWrapper('SchedulerService')
export const SettingsApi = createSdkWrapper('SettingsService')
export const PrivacyApi = createSdkWrapper('PrivacyService')
export const SourceRatesApi = createSdkWrapper('SourceRatesService')
export const SourceRatesApiLegacy = SourceRatesApi
export const SummariesApi = createSdkWrapper('SummariesService')
export const SupplierRatesApi = createSdkWrapper('SupplierRatesService')
export const UploadsApi = createSdkWrapper('UploadsService')
export const UserPartnersApi = createSdkWrapper('UserPartnersService')
export const UsersApi = createSdkWrapper('UsersService')
export const AiClaimsApi = createSdkWrapper('AiclaimsService')
export const SecurityTestsApi = createSdkWrapper('SecurityTestsService')
export const VehGroupsApi = createSdkWrapper('VehGroupsService')
export const XeroApi = createSdkWrapper('XeroService')

export const AdministrationService = AdministrationApi
export const AiPromptConfigsService = AiPromptConfigsApi
export const AnniesService = AnniesApi
export const ApiBillingService = ApiBillingApi
export const AuthoritiesService = AuthoritiesApi
export const CircsGradesService = CircsGradesApi
export const ClaimsService = ClaimsApi
export const ComplaintsService = ComplaintsApi
export const InsurersService = InsurersApi
export const InvoicesService = InvoicesApi
export const LoginService = LoginApi
export const OtpService = OtpApi
export const PartnerRulesService = PartnerRulesApi
export const PartnersService = PartnersApi
export const RateLimitingService = RateLimitingApi
export const ReferralAllocationsService = ReferralAllocationsApi
export const ReferralNotesService = ReferralNotesApi
export const ReferralsService = ReferralsApi
export const RepairBillingService = RepairBillingApi
export const ReportTemplatesService = ReportTemplatesApi
export const RequestsService = RequestsApi
export const RoleAllocationsService = RoleAllocationsApi
export const RolePermissionsService = RolePermissionsApi
export const RolesService = RolesApi
export const SchedulerService = SchedulerApi
export const SettingsService = SettingsApi
export const SourceRatesService = SourceRatesApi
export const SummariesService = SummariesApi
export const SupplierRatesService = SupplierRatesApi
export const UploadsService = UploadsApi
export const UserPartnersService = UserPartnersApi
export const UsersService = UsersApi
export const AiclaimsService = AiClaimsApi
export const SecurityTestsService = SecurityTestsApi
export const VehGroupsService = VehGroupsApi
export const XeroService = XeroApi
export const PermissionsService = createSdkWrapper('PermissionsService')
export const AgentRunsService = createSdkWrapper('AgentRunsService')
export const WorkflowFeedbackAdminService = createSdkWrapper(
  'WorkflowFeedbackAdminService',
)
export const WorkflowDefinitionsService = createSdkWrapper(
  'WorkflowDefinitionsService',
)
export const MetabaseReportsService = createSdkWrapper('MetabaseReportsService')

export type GtaCorrectionRecord = {
  id: number
  fieldKey: string
  oldValue?: string | null
  newValue?: string | null
  feedbackResponseId?: string | null
  userId?: number | null
  baselineTaskId?: string | null
  createdAt: string
}

export type GtaInternalActionRecord = {
  status: string
  check?: string | null
  note: string
  source: string
}

export type GtaSnapshotRecord = {
  referralId: number
  sourceRef?: string | null
  profileKey: string
  fields: Record<string, unknown>
  fieldsMeta?: Record<string, { source?: string }> | null
  actions?: GtaInternalActionRecord[]
  baselineTaskId?: string | null
  lastWorkflowKey?: string | null
  latestSummaryId?: number | null
  latestTaskId?: string | null
  fieldCatalog: GtaFieldCatalogItem[]
  corrections: GtaCorrectionRecord[]
}

export type GtaReportRecord = {
  uploadId: number
  fileName: string
  createdAt?: string | null
  runLabel: string
  workflowKey: string
  summaryId?: number | null
  taskId?: string | null
}

export type GtaReportsResponse = {
  data: GtaReportRecord[]
  count: number
}

export class GtaApi {
  getReferralSnapshot(referralId: number): Promise<GtaSnapshotRecord> {
    return sdkData(
      getGtaReferralSnapshot({
        path: { referral_id: referralId },
      }),
    ) as Promise<GtaSnapshotRecord>
  }

  listReferralReports(referralId: number): Promise<GtaReportsResponse> {
    return sdkData(
      listGtaReferralReports({
        path: { referral_id: referralId },
      }),
    ) as Promise<GtaReportsResponse>
  }

  patchReferralField(
    referralId: number,
    fieldKey: string,
    body: { value: unknown; comment: string; reasonCode?: string },
  ): Promise<GtaSnapshotRecord> {
    return sdkData(
      patchGtaReferralField({
        path: { referral_id: referralId, field_key: fieldKey },
        body,
      }),
    ) as Promise<GtaSnapshotRecord>
  }
}
